// Enhanced Font System for Mappin
// This file provides semantic font tokens for easy universal font management

export const fonts = {
  // Font families
  families: {
    primary: 'Red Rose, Noto Sans, Helvetica, sans-serif',
    secondary: 'Poppins, Arial, sans-serif',
    display: 'Poppins, Arial, sans-serif',
    monospace: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  
  // Font weights
  weights: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  
  // Font sizes (in rem for scalability)
  sizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
    '6xl': '3.75rem', // 60px
    '7xl': '4.5rem',  // 72px
  },
  
  // Line heights
  lineHeights: {
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
  
  // Letter spacing
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0em',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
  
  // Semantic font combinations for common use cases
  semantic: {
    // Headings
    h1: {
      fontFamily: 'Red Rose, Noto Sans Display, Lato, sans-serif',
      fontSize: '3rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontFamily: 'Red Rose, Noto Sans Display, Lato, sans-serif',
      fontSize: '2.25rem',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontFamily: 'Red Rose, Noto Sans Display, Lato, sans-serif',
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.375,
      letterSpacing: 'normal',
    },
    h4: {
      fontFamily: 'Red Rose, Lato, Helvetica, sans-serif',
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.375,
      letterSpacing: 'normal',
    },
    h5: {
      fontFamily: 'Red Rose, Lato, Helvetica, sans-serif',
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: 'normal',
    },
    h6: {
      fontFamily: 'Red Rose, Lato, Helvetica, sans-serif',
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: 'normal',
    },
    
    // Body text
    body: {
      fontFamily: 'Poppins, Lato, Helvetica, sans-serif',
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 'normal',
    },
    bodyLarge: {
      fontFamily: 'Poppins, Lato, Helvetica, sans-serif',
      fontSize: '1.125rem',
      fontWeight: 400,
      lineHeight: 1.625,
      letterSpacing: 'normal',
    },
    bodySmall: {
      fontFamily: 'Poppins, Lato, Helvetica, sans-serif',
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 'normal',
    },
    
    // UI elements
    button: {
      fontFamily: 'Red Rose, Lato, Helvetica, sans-serif',
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.025em',
    },
    caption: {
      fontFamily: 'Poppins, Helvetica, sans-serif',
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.375,
      letterSpacing: '0.025em',
    },
    label: {
      fontFamily: 'Poppins, Lato, Helvetica, sans-serif',
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.375,
      letterSpacing: '0.025em',
    },
    
    // Special use cases
    code: {
      fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 'normal',
    },
    display: {
      fontFamily: 'Red Rose, Noto Sans Display, Lato, sans-serif',
      fontSize: '4.5rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.05em',
    },
  },
} as const;

// Legacy font exports for backward compatibility
export const primaryFont = fonts.families.primary;
export const secondaryFont = fonts.families.secondary;
export const displayFont = fonts.families.display;
export const monospaceFont = fonts.families.monospace;

// Common font combinations for easy access
export const headingFont = fonts.semantic.h1;
export const bodyFont = fonts.semantic.body;
export const buttonFont = fonts.semantic.button;
export const codeFont = fonts.semantic.code;

